import { Box, Grid, Typography } from '@material-ui/core';
import axios, { CancelTokenSource } from 'axios';
import {
  ButtonText,
  colors,
  DefaultButton,
  MessageBox,
  Modal,
  PhotoCard,
  Signature,
} from 'design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// Models
import {
  faCircleExclamation,
  faCircleInfo,
  faCircleXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CAR_PHOTOS_STEP, trackCurrentStep, trackEvent } from 'src/analytics';
import TakenImages from '../../../../models/TakenImages';
import UploadFileState from '../../../../models/helperTypes/FileUploadState';

// Hooks
import usePhotoCapturer from '../../../PhotoCapturer/usePhotoCapturer';

// Redux
import {
  registerCarImage,
  setUploadStateMap,
  setVehicleRegistryImage,
  uploadSingleFile,
} from '../../../../store/inspectionForm/actions';
import { UploadStateMap } from '../../../../store/inspectionForm/slice';
import { RootState } from '../../../../store/rootReducer';

// Components
import { RegistryUploadStatus } from '../../components';
import PictureList from '../components/PictureList';
import TakePictureSonar, { SonarStates } from '../components/TakePictureSonar';

// Extra
import { cameraStyles } from '../../../../theme/cameraStyles';

const CAR_IMAGE_WIDTH = 167;
const CAR_IMAGE_HEIGHT = 316;

export const REGISTRY_IDENTIFIER = 'vehicle-registry-image';

/** Picture parameters for TakePictureSonar components */
const takePictureSonarParams: {
  absolutePosition: {
    left: number;
    top: number;
  };
  displayText: string;
  section: string;
  id: string;
}[] = [
  {
    absolutePosition: {
      left: CAR_IMAGE_WIDTH / 2,
      top: -50,
    },
    displayText: 'Frontal',
    section: 'frontal',
    id: 'sales-web-inspection-button-photofront',
  },
  {
    absolutePosition: {
      left: CAR_IMAGE_WIDTH / 2,
      top: CAR_IMAGE_HEIGHT / 2 + 30,
    },
    displayText: 'Tablero (mostrar kilometraje)',
    section: 'interior',
    id: 'sales-web-inspection-button-photoinside',
  },
  {
    absolutePosition: {
      left: CAR_IMAGE_WIDTH / 2,
      top: CAR_IMAGE_HEIGHT / 5.7,
    },
    displayText: 'Interior(VIN)',
    section: 'windshield',
    id: 'sales-web-inspection-button-photoVIN',
  },
  {
    absolutePosition: {
      left: CAR_IMAGE_WIDTH / 2,
      top: CAR_IMAGE_HEIGHT + 40,
    },
    displayText: 'Trasera',
    section: 'back',
    id: 'sales-web-inspection-button-photoback',
  },
  {
    absolutePosition: {
      left: -30,
      top: CAR_IMAGE_HEIGHT / 2,
    },
    displayText: 'Izquierda',
    section: 'left',
    id: 'sales-web-inspection-button-photoleft',
  },
  {
    absolutePosition: {
      left: CAR_IMAGE_WIDTH + 30,
      top: CAR_IMAGE_HEIGHT / 2,
    },
    displayText: 'Derecha',
    section: 'right',
    id: 'sales-web-inspection-button-photoright',
  },
];

function shouldUpload(state: UploadFileState): boolean {
  return ['retry', 'initial'].includes(state);
}

// extracted to avoid SonarCloud cognitive complexity restriction
// all images need to exist and all upload states needs to be successful
function shouldDisableNextButton(
  carImages: TakenImages,
  uploadStateMap: UploadStateMap,
  vehicleRegistryImage: string,
): boolean {
  return (
    [
      carImages.frontal,
      carImages.right,
      carImages.left,
      carImages.back,
      carImages.interior,
      vehicleRegistryImage,
    ].includes('') ||
    Object.keys(uploadStateMap).length !== 7 ||
    Object.keys(uploadStateMap).some((key) => uploadStateMap[key] !== 'success')
  );
}

export const InspectionPhoto = ({
  carImages,
  uploadStateMap,
  vechicleRegistryFileName,
  vehicleRegistryImage,
  registerCarImageAction,
  setVehicleRegistryImageAction,
  setUploadStateMapAction,
  uploadSingleFileAction,
  onSubmit,
}: Props): JSX.Element => {
  const classes = cameraStyles();
  const [openModal, setOpenModal] = useState(false);
  const [registryCancelTokenSource, setRegistryCancelTokenSource] =
    useState<CancelTokenSource>(axios.CancelToken.source());
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /** Trigger upload for files in "retry" or "initial" states */
  useEffect(() => {
    Object.keys(uploadStateMap).forEach((key) => {
      if (
        shouldUpload(uploadStateMap[key]) &&
        Object.keys(carImages).includes(key)
      ) {
        // trigger upload attempt car images
        uploadSingleFileAction(key, carImages[key]);
      }
    });
  }, [uploadStateMap, carImages, uploadSingleFileAction]);

  /**
   * Whenever Vehicle Registry upload fails, remove it
   * Whenever Vehicle Registry is empty, reset upload state
   */
  useEffect(() => {
    if (
      Object.keys(uploadStateMap).includes(REGISTRY_IDENTIFIER) &&
      uploadStateMap[REGISTRY_IDENTIFIER] === 'failed'
    ) {
      setVehicleRegistryImageAction('', '');
    }
  }, [uploadStateMap, setVehicleRegistryImageAction]);

  /**
   * Returns true if no images have been taken
   */
  function noImagesTaken(): boolean {
    return Object.keys(carImages).length <= 0;
  }

  /**
   * Called when the camera successfully captures an image
   *
   * @param identifier - The image's unique identifier
   * @param base64image - The image's base64 representation
   */
  function onPhotoCaptured(identifier: string, base64image: string): void {
    /** Register car image and its corresponding upload status */
    registerCarImageAction(identifier, base64image);
    setUploadStateMapAction({ [identifier]: 'initial' });
  }

  function handleRegistryFileCaptured(
    fileName: string,
    base64Image: string,
  ): void {
    setVehicleRegistryImageAction(fileName, base64Image);
    setUploadStateMapAction({ [REGISTRY_IDENTIFIER]: 'initial' });
    /** Only attempt to upload non-empty base64Image */
    if (base64Image !== '') {
      uploadSingleFileAction(
        REGISTRY_IDENTIFIER,
        base64Image,
        registryCancelTokenSource,
      );
    }
  }

  function handleCancelRegistryUpload(): void {
    registryCancelTokenSource.cancel();
    setUploadStateMapAction({ [REGISTRY_IDENTIFIER]: 'failed' });

    // assign new cancel token source
    setRegistryCancelTokenSource(axios.CancelToken.source());
  }

  /**
   * Returns the required state for the sonar name. Note that
   * each sonar must have a unique identifier associated with it.
   *
   * @param name - The sonar's unique name
   */
  function sonarStateForName(name: string): SonarStates {
    if (
      Object.keys(carImages).includes(name) &&
      uploadStateMap[name] === 'success'
    ) {
      return SonarStates.Completed;
    }
    if (noImagesTaken()) {
      return name === 'frontal' ? SonarStates.Active : SonarStates.Disabled;
    }
    return SonarStates.Active;
  }

  const { cameraView, requestImage } = usePhotoCapturer(onPhotoCaptured);
  const isDisabled = useMemo(
    () =>
      shouldDisableNextButton(
        carImages,
        uploadStateMap,
        vechicleRegistryFileName,
      ) || !signed,
    [carImages, uploadStateMap, vechicleRegistryFileName, signed],
  );

  return (
    <>
      <Box mx="0.5rem" mt="1rem" mb="0.8rem">
        <MessageBox
          data-testid="information-message"
          message={
            <Typography variant="caption" style={{ fontWeight: 700 }}>
              Debes tomar las fotos directamente a tu auto. No son válidas fotos
              tomadas a otras fotos o que no corresponden a tu auto.
            </Typography>
          }
          variant="info"
          hideCloseButton
        />
      </Box>
      {cameraView}
      {openModal && (
        <Modal open={openModal} wrapperStyles={classes.modal} fullWidth>
          <Box width="100%" display="flex" flexDirection="column">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{
                gap: '1rem',
                padding: '1rem',
                paddingTop: '1.5rem',
              }}
              position="relative"
              bgcolor={colors.white}
            >
              <Typography variant="h6">¿Dónde encontrar el VIN?</Typography>
              <Box
                position="absolute"
                top="26px"
                right="1rem"
                style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon
                  data-testid="close-modal-button"
                  icon={faCircleXmark}
                  size="xl"
                  color={colors.madison}
                  onClick={() => setOpenModal(false)}
                />
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              style={{
                gap: '1rem',
                paddingInline: '1.5rem',
                paddingBlock: '1rem',
                height: 'calc(100vh - 65px)',
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                style={{ gap: '1rem' }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  style={{ gap: '4rem' }}
                >
                  <div className={classes.aviseVinContent}>
                    <Box position="relative" top="4px">
                      <FontAwesomeIcon
                        icon={faCircleExclamation}
                        size="lg"
                        color={colors.madison}
                      />
                    </Box>
                    <Typography variant="caption" style={{ fontWeight: 500 }}>
                      Puedes buscar el VIN o número de chasis de tu auto en los
                      siguientes lugares o consultar el manual de tu vehículo
                      para localizarlo:
                    </Typography>
                  </div>
                  <img
                    src="./svg/whereFindVin.svg"
                    alt="donde encontrar el vin"
                    style={{ width: '100%' }}
                  />
                </Box>
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                style={{ paddingInline: '1rem' }}
              >
                <DefaultButton onClick={() => setOpenModal(false)} fullWidth>
                  Entendido
                </DefaultButton>
              </Box>
            </Box>
          </Box>
        </Modal>
      )}
      <Grid container className={classes.gridWrapper} spacing={3}>
        {!noImagesTaken() && (
          <Grid item xs={12}>
            <PictureList
              data-testid="picture-list"
              images={carImages}
              uploadStateMap={uploadStateMap}
              onRetryUpload={uploadSingleFileAction}
              onCancelUpload={setUploadStateMapAction}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          container
          alignItems="center"
          justifyContent="center"
        >
          <div
            style={{
              width: CAR_IMAGE_WIDTH,
              height: CAR_IMAGE_HEIGHT,
              marginTop: 90,
              marginBottom: CAR_IMAGE_HEIGHT / 3,
              position: 'relative',
            }}
          >
            {/* Render carousel of pictures taken */}
            {noImagesTaken() && (
              <div
                data-testid="camera-call-to-action"
                className={classes.cameraCallToAction}
              >
                <div className={classes.cameraCallToActionArrow} />
                Toca para activar la cámara
              </div>
            )}
            <img alt="Car Model" src="./images/placeholder_car.png" />

            {/* Render Sonar Buttons */}
            {takePictureSonarParams.map((ele) => (
              <TakePictureSonar
                key={ele.displayText}
                absolutePosition={ele.absolutePosition}
                displayText={ele.displayText}
                sonarState={sonarStateForName(ele.section)}
                onImageRequest={(): void => requestImage(ele.section)}
                id={ele.id}
                section={ele.section}
                onPhotoCaptured={onPhotoCaptured}
              />
            ))}
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.aviseVin}>
            <div className={classes.aviseVinContent}>
              <Box position="relative" top="2px">
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  size="lg"
                  className={classes.aviseVinIcon}
                />
              </Box>
              <Typography className={classes.aviseVinText}>
                ¿Dónde puedo encontrar el VIN de mi auto?
              </Typography>
            </div>
            <ButtonText
              className={classes.aviseVinButton}
              onClick={() => setOpenModal(true)}
            >
              Ver más
            </ButtonText>
          </div>
        </Grid>

        {/* The registry photo */}
        <Grid item xs={12} data-testid="registry-card">
          <PhotoCard
            id="sales-web-inspection-button-registrycard"
            data-testid="vehicle-registry-card"
            title="Foto del registro vehicular"
            subTitle="Incluya una foto de su registro vehicular"
            fileName={vechicleRegistryFileName}
            base64String={vehicleRegistryImage}
            onLocalImageCaptured={handleRegistryFileCaptured}
            confirmationMessage="¿Estás seguro que deseas eliminar el registro vehicular?"
            disabled={uploadStateMap[REGISTRY_IDENTIFIER] === 'uploading'}
          />
        </Grid>
        <RegistryUploadStatus
          data-testid="registry-status"
          status={uploadStateMap[REGISTRY_IDENTIFIER] || 'initial'}
          onCancelAction={handleCancelRegistryUpload}
        />
        <Grid item xs={12} container justifyContent="center">
          <Signature
            defaultValue={signed}
            onChange={(val): void => setSigned(val)}
            checkBoxId="sales-web-inspection-checkbox-customersignature"
            disclaimerId="sales-web-inspection-button-opendisclaimers"
            data-testid="signature"
          />
        </Grid>
        <Grid
          item
          xs={12}
          className={classes.nextButton}
          container
          alignItems="center"
          justifyContent="center"
        >
          <DefaultButton
            id="sales-web-inspection-button-next"
            data-testid="next-button"
            onClick={(): void => {
              trackEvent({
                eventName: CAR_PHOTOS_STEP,
                eventProperties: {},
              });
              trackCurrentStep(CAR_PHOTOS_STEP);
              onSubmit();
            }}
            disabled={isDisabled}
            fullWidth
            customColor={{
              default: colors.bright_turquoise,
              hover: colors.onahau,
              active: colors.bright_turquoise,
            }}
          >
            Enviar Inspección
          </DefaultButton>
        </Grid>
      </Grid>
    </>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  carImages: TakenImages;
  uploadStateMap: UploadStateMap;
  vechicleRegistryFileName: string;
  vehicleRegistryImage: string;
} => ({
  carImages: state.inspectionForm.carImages,
  uploadStateMap: state.inspectionForm.uploadStateMap,
  vechicleRegistryFileName: state.inspectionForm.vehicleRegistryFileName,
  vehicleRegistryImage: state.inspectionForm.vehicleRegistryImage,
});

const reduxActions = {
  registerCarImageAction: registerCarImage,
  setVehicleRegistryImageAction: setVehicleRegistryImage,
  setUploadStateMapAction: setUploadStateMap,
  uploadSingleFileAction: uploadSingleFile,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(React.memo(InspectionPhoto));
